"use client";

import React, { useLayoutEffect, useState } from "react";
import { useFeatureFlags } from "@shared-components/feature-flag-context";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@shared-components/shadcn/popover";
import { TriangleAlert } from "lucide-react";

/**
 * Show the current feature's degraded-state reasons next to the top-bar children.
 * Renders the children unchanged when the feature is healthy or no feature
 * context is mounted. Each reason is one notice that fits the available width.
 */
export const DegradedFeatureFlagFallback = (props: React.PropsWithChildren) => {
  const featureFlags = useFeatureFlags();

  if ("contextUnAvailable" in featureFlags || !featureFlags.featureIsDegraded) {
    return <>{props.children}</>;
  }

  return (
    <>
      {props.children}
      <div className="sm:ml-7 flex min-w-0 flex-wrap gap-2">
        {featureFlags.reasons.map((reason, idx) => (
          <DegradedFeatureNotice key={idx} reason={reason} />
        ))}
      </div>
    </>
  );
};

/**
 * One degraded-feature reason as a warning pill. On small screens the text is
 * capped in width and cut with an ellipsis; on desktop it uses the space left
 * in the top bar before truncating. Only a truncated pill is clickable and
 * opens the full reason, so a reason that fits has no tooltip or popover.
 *
 * @param props.reason - The admin-entered explanation of the degradation.
 */
export const DegradedFeatureNotice = (props: { reason: string }) => {
  // A callback ref keeps the element in state, so the observer re-attaches
  // when the pill swaps between its plain and clickable forms. The shared
  // useObserver hook reads the ref during render and would keep watching the
  // unmounted span after that swap.
  const [textElement, setTextElement] = useState<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    if (!textElement) return;

    // The text span keeps `truncate`, so overflow means the ellipsis is showing.
    const measure = () =>
      setIsTruncated(textElement.scrollWidth > textElement.clientWidth);

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(textElement);

    return () => observer.disconnect();
  }, [textElement]);

  const pill = (
    <>
      <TriangleAlert aria-label="Warning" size={14} className="shrink-0" />
      <span ref={setTextElement} className="truncate">
        {props.reason}
      </span>
    </>
  );

  if (!isTruncated) {
    return <span className={PILL_CLASS_NAME}>{pill}</span>;
  }

  return (
    <Popover>
      <PopoverTrigger className={PILL_CLASS_NAME} title={props.reason}>
        {pill}
      </PopoverTrigger>
      <PopoverContent align="start" className="text-sm">
        {props.reason}
      </PopoverContent>
    </Popover>
  );
};

const PILL_CLASS_NAME =
  "flex min-w-0 max-w-[11rem] items-center gap-1.5 rounded-md border border-sec-black/20 bg-sec-bright-pink px-1.5 py-0.5 text-left text-sm text-white sm:max-w-sm md:max-w-full dark:bg-sec-light-pink dark:text-black";
